import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Badge, Button, Spinner } from 'react-bootstrap';
import { 
    ShieldCheck, Building2, Users, CreditCard, HardDrive, 
    Clock, TrendingUp, IndianRupee, Shield 
} from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';
import { Line } from 'react-chartjs-2';
import { io } from 'socket.io-client';
import AdminChatPanel from './AdminChatPanel';
import LiveConversations from './LiveConversations';

// Core Components
import TaxSummaryReport from '../admin/TaxSummaryReport';
import DeductionForecast from '../../components/admin/DeductionForecast';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
    CategoryScale, 
    LinearScale, 
    PointElement, 
    LineElement, 
    Title, 
    Tooltip, 
    Legend, 
    ArcElement
);

const socket = io("http://localhost:5000");

const SuperDashboard = () => {
    const [data, setData] = useState({ 
        stats: {}, 
        growth: [], 
        companies: [], 
        health: {} 
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        socket.on('admin_receive_message', (data) => {
            if (data.play_sound) {
                const audio = new Audio('/sounds/notification-beep.mp3');
                audio.play().catch(e => console.log("Audio play blocked"));
            }
        });
        return () => socket.off('admin_receive_message');
    }, []);

    useEffect(() => {
        const fetchSuperData = async () => {
            try {
                const statsRes = await axiosInstance.get('/superadmin/global-stats');
                const healthRes = await axiosInstance.get('/superadmin/health');
                const compRes = await axiosInstance.get('/superadmin/tenants');

                setData({ 
                    stats: statsRes.data.data.stats || {}, 
                    growth: statsRes.data.data.growth || [], 
                    health: healthRes.data.data || {},
                    companies: compRes.data.data || [] 
                });
            } catch (err) { 
                console.error("Dashboard Loading Error:", err); 
            } finally { 
                setLoading(false); 
            }
        };
        fetchSuperData();
    }, []);

    const chartData = {
        labels: data.growth.map(g => g.month).reverse(),
        datasets: [
            {
                label: 'New Companies',
                data: data.growth.map(g => g.count).reverse(),
                fill: false,
                borderColor: '#4f46e5',
                tension: 0.3,
                pointBackgroundColor: '#4f46e5'
            }
        ]
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="super-admin-dashboard p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold mb-0 d-flex align-items-center gap-2">
                    <ShieldCheck className="text-primary" /> Super Admin Command Center
                </h4>
                <Badge bg="primary" className="bg-opacity-10 text-primary px-3 py-2 rounded-pill">
                    System Version v2.4.0
                </Badge>
            </div>

            {/* System Health Section */}
            <Row className="mb-4 g-3">
                <Col md={3}>
                    <Card className="border-0 shadow-sm p-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="p-2 bg-success bg-opacity-10 rounded-circle">
                                <HardDrive className="text-success" size={20} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold d-block text-uppercase" style={{fontSize: '0.65rem'}}>Database</small>
                                <span className="fw-bold text-success">Healthy</span>
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm p-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="p-2 bg-info bg-opacity-10 rounded-circle">
                                <Clock className="text-info" size={20} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold d-block text-uppercase" style={{fontSize: '0.65rem'}}>Uptime</small>
                                <span className="fw-bold">{Math.round(data.health.uptime / 3600) || 0} Hours</span>
                            </div>
                        </div>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm p-3 bg-primary text-white">
                        <small className="text-uppercase fw-bold opacity-75" style={{fontSize: '0.65rem'}}>Total Payouts</small>
                        <h4 className="fw-bold mb-0">₹{data.stats.total_payouts_processed?.toLocaleString() || 0}</h4>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm p-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="p-2 bg-primary bg-opacity-10 rounded-circle">
                                <Shield className="text-primary" size={20} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold d-block text-uppercase" style={{fontSize: '0.65rem'}}>Security Status</small>
                                <span className="fw-bold text-primary">Active Protection</span>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Financial Oversight - Reports Integrated Here */}
            <div className="mb-5">
                <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                    <IndianRupee size={20} className="text-primary" /> Financial Oversight
                </h5>
                <Row className="g-4">
                    <Col lg={12}>
                        <TaxSummaryReport />
                    </Col>
                    <Col lg={8}>
                        <DeductionForecast />
                    </Col>
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm p-4 h-100 bg-dark text-white">
                            <h6 className="fw-bold mb-3 text-uppercase small opacity-75">Compliance Status</h6>
                            <div className="mt-3">
                                <div className="d-flex justify-content-between mb-3 small align-items-center">
                                    <span>TDS Returns (Q4)</span>
                                    <Badge bg="success">Filed</Badge>
                                </div>
                                <div className="d-flex justify-content-between mb-3 small align-items-center">
                                    <span>PF Deposits</span>
                                    <Badge bg="warning" className="text-dark">Due in 5d</Badge>
                                </div>
                                <div className="d-flex justify-content-between mb-3 small align-items-center">
                                    <span>PT Submission</span>
                                    <Badge bg="info">Processing</Badge>
                                </div>
                            </div>
                            <Button variant="outline-light" size="sm" className="mt-auto w-100">
                                View Compliance Calendar
                            </Button>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Growth Analytics */}
            <Row className="g-4 mb-5">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Header className="bg-white py-3 border-0">
                            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                <TrendingUp size={18} className="text-primary" /> Tenant Growth Analytics
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <div style={{ height: '300px' }}>
                                <Line data={chartData} options={{ maintainAspectRatio: false, responsive: true }} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                
                <Col lg={4}>
                    <Card className="border-0 shadow-sm h-100 p-4">
                        <h6 className="fw-bold mb-4">Quick Management</h6>
                        <div className="d-grid gap-3">
                            <Button variant="outline-primary" className="text-start py-2 px-3">Backup System Database</Button>
                            <Button variant="outline-dark" className="text-start py-2 px-3">Review Server Logs</Button>
                            <Button variant="primary" className="text-start py-2 px-3">Onboard New Tenant</Button>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Tenant Table & Chat */}
            {/* <Row className="g-4">
                <Col lg={12}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white py-3 border-0">
                            <h6 className="mb-0 fw-bold">Active Managed Organizations</h6>
                        </Card.Header>
                        <Table hover responsive className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr className="small text-muted text-uppercase">
                                    <th>Company Name</th>
                                    <th>Status</th>
                                    <th>Plan</th>
                                    <th>Contact</th>
                                    <th>Joined</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.companies.map(company => (
                                    <tr key={company.id}>
                                        <td className="fw-bold">{company.name}</td>
                                        <td>
                                            <Badge bg={company.status === 'active' ? 'success' : 'secondary'} className="bg-opacity-10 text-success">
                                                {(company.status || 'Active').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>{company.plan_type || 'Basic'}</td>
                                        <td className="small text-muted">{company.email}</td>
                                        <td>{new Date(company.created_at).toLocaleDateString()}</td>
                                        <td className="text-end">
                                            <Button variant="link" size="sm" className="text-decoration-none">Audit</Button>
                                            <Button variant="outline-primary" size="sm" className="ms-2">Manage</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </Col>

                <Col lg={12} className="mt-5">
                    <AdminChatPanel />
                </Col>
            </Row> */}
            <LiveConversations socket={socket} />
        </div>
    );
};

export default SuperDashboard;